// Google Analytics 4 wrapper for the web app.
//
// Coexists with PostHog (product analytics + session replay) and Sentry
// (errors). GA4 is the SEO + funnel + Google-Ads-attribution layer.
//
// Property: wefaqna-5489c (id 519262510 — also receives mobile events
// via Firebase Analytics).
// Web stream Measurement ID: G-WQT2TTWGFT (public-safe — designed to be
// embedded in the client bundle).
//
// gtag.js is loaded from public/index.html. This wrapper just provides
// a typed-ish API that mirrors services/posthog.js so call sites stay
// symmetric (initX → identifyUserX → trackEventX → resetX).
//
// Kill switch: if window.gtag is undefined (e.g. ad-blocker, network
// failure, or we remove the script tag), every call no-ops without
// throwing. Telemetry must never crash UI.

const MEASUREMENT_ID = "G-WQT2TTWGFT";
let _initialized = false;

function _gtagReady() {
  return typeof window !== "undefined" && typeof window.gtag === "function";
}

/**
 * Initialise. Safe to call multiple times — second call is a no-op.
 * Reads no env vars: the measurement ID is a public ID that ships
 * with the bundle (same as Firebase config IDs in the mobile app).
 */
export function initGA4() {
  if (_initialized) return;
  if (!_gtagReady()) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.info("[GA4] gtag.js not present — telemetry disabled");
    }
    return;
  }
  _initialized = true;
}

/**
 * Fire a manual page_view event for SPA route changes.
 * The initial page load is captured by gtag('config') in index.html
 * (send_page_view: false there so we control routing in React).
 */
export function trackPageView(path) {
  if (!_initialized || !_gtagReady()) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: MEASUREMENT_ID,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[GA4] trackPageView failed", err);
  }
}

/**
 * Capture a custom event. GA4 has reserved event names (sign_up, login,
 * purchase, etc.) that show up as "conversions" in the dashboard — use
 * those exact names where they fit.
 */
export function trackEvent(name, params = {}) {
  if (!_initialized || !_gtagReady()) return;
  try {
    window.gtag("event", name, { ...params, send_to: MEASUREMENT_ID });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[GA4] trackEvent failed", name, err);
  }
}

/**
 * Tag the session with a user_id after login. Mirrors the mobile
 * Firebase Analytics setUserId contract.
 *
 * GA4 hashes the user_id server-side — no PII leaves the browser as
 * long as the userId is an internal opaque ID (not email/phone).
 */
export function identifyUserGA4(userId, properties = {}) {
  if (!_initialized || !_gtagReady()) return;
  try {
    window.gtag("config", MEASUREMENT_ID, {
      user_id: String(userId),
      // user_properties live longer than per-event params — good for
      // segmenting cohorts (profile_preference, has_form, governorate)
      user_properties: properties,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[GA4] identifyUser failed", err);
  }
}

/**
 * Drop the user_id after logout. New events return to anonymous.
 */
export function resetGA4() {
  if (!_initialized || !_gtagReady()) return;
  try {
    window.gtag("config", MEASUREMENT_ID, { user_id: null });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[GA4] reset failed", err);
  }
}
